import * as React from 'react';
import _ from 'lodash';

import { WebNode } from '@API/WebNode';
import type { WebModule } from '@API/WebModule';
import type { WebThing } from '@API/WebThing';

import { DashboardManagerParent } from '@UI/Dashboard/DashboardManagerParent';
import { IWidgetClass, IWidgetDescription, WidgetThingGenericConfiguration, WidgetType } from '@UI/Widgets/WidgetStructs';
import type { IWidgetDialog } from "@UI/Widgets/IWidgetClassParent";
import type { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetThings, IWidgetThingsBaseComponent, WidgetDataTransform, WidgetLocalizationKeys, WidgetThingsSettings } from '../WidgetsThingsStructs';
import { Sandbox } from '@Utils/Sandbox/Sandbox';
import { DebugEx } from '@Utils/DebugEx';
import { Localization, LocalizationMap } from '@Primitive/Localization/Localization';
import { LastUpdateTs, __LAST_UPDATE_TS_KEY__ } from './LastUpdateTs';
import { ITsViewFormat } from '@ModelViews/ModelViews';
import { LastUpdateTsPrimitive } from './LastUpdateTsPrimitive';
import { MarkdownProps } from '@Primitive/Markdown/MarkdownLazy';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { ToolbarIconInfo, ToolbarIconInfo_ButtonRender } from '@Primitive/ToolbarIconButton';
import { WebThingKeys } from '@API/WebNodeTypes';

const DebugPortList = React.lazy(() => import('./DebugPortList'));
const DebugWidgetGemsList = React.lazy(() => import('./DebugWidgetGemsList'));
const WidgetDescriptionAndPlayground = React.lazy(() => import('./WidgetDescriptionAndPlayground'));

// ------------------------------------------------------------------------------------------------
export class IWidgetThingsClass<T> extends IWidgetClass<T> implements IWidgetThings {
    static whyDidYouRender = true;
    // ------------------------------------------------------------------------------------------------
    // Work with keys and get from singleton node the structures
    public NodeKey: string;
    public ThingKey: string;
    public WebModuleKey: string;
    public TabsWidgets: { [key: string]: IWidgetThingsClass<any> };

    get Thing(): WebThing { return WebNode.GetInstanceKey(this.NodeKey).GetThing(this.ThingKey); }
    get WebModule(): WebModule { return WebNode.GetInstanceKey(this.NodeKey).GetModule(this.WebModuleKey); }
    get Parent() {
        let parent = DashboardManagerParent.GetInstance(this.NodeKey);
        if (isDefined(parent))
            return parent.Info;
        else
            return DashboardManagerParent.GetDefaultInfo();
    }
    // ------------------------------------------------------------------------------------------------
    constructor(description: IWidgetDescription, data: any, webModule: WebModule) {
        super(description, data);
        this.ForceRenderInResize = true; // We need the client to set this for each thing

        this.NodeKey = webModule.NodeKey;
        this.WebModuleKey = webModule.ModuleKey;
        this.WidgetType = WidgetType.Thing;

        // Init thing
        this.initThing();

        // Try to tranform the data
        this.Thing.onDataTransform = (portKey: string, data: any, outDataCB: (portKey: string, data: any) => void) => {
            // Transform the data if we have custom form functions
            if (_.has(this.Description.PortMacros, portKey) &&
                _.has(this.Description.PortMacros[portKey], "onDataTransform") &&
                this.Description.PortMacros[portKey]["onDataTransform"].Enable) {
                Sandbox.GetInstance("WidgetThingsJSOutputRunner").Eval(
                    this.Description.PortMacros[portKey]["onDataTransform"].Code,
                    { PortState: data, ExtraInfo: null, WidgetData: this.WidgetData } as WidgetDataTransform,
                    (newState) => {
                        outDataCB(portKey, newState);
                    },
                    (error) => {
                        DebugEx.TraceError(error);
                    }
                );
                return true;
            }
            return false;
        }

        // Init localization dictionary
        this.UpdateLocalization();
    }

    // ================================================================================================ override abstract functions +++
    /**
     * Called to init Thing
     * @returns void
     */
    initThing(): void { }
    /**
     * Called when widget gets new `WidgetData`. Can be used to update Thing.
     * @returns void
     */
    updateThing(): void { }
    Render(key: string, width: number, height: number): React.ReactNode { return null; };
    RenderToolbar(key: string, toolbarHeight: number): React.ReactNode[] { return null; };
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactNode { return null; }
    GetDefaultWidgetData(): T { return {} as T; }
    GetDefaultWidgetDataStatic(): T { return {} as T; }
    MigrateDefaultWidgetData(data: T): T { return data; }
    GetDefaultValues(): { [key: string]: any } { return {}; }
    ValidateValues(key: string, value: any): IWidgetDataValidateResp { return { valid: true, errors: "" } };
    GetWidgetDesc(): IWidgetDescription { return null; };
    GetLocalizationDictionary(): LocalizationMap { return {} };
    GetDataPortKeyStatic(): string { return null; };
    // ================================================================================================ override abstract functions ---

    // ================================================================================================ wrapper functions to widget +++
    // --------------------------------------------------------------------------------------------
    RenderBase(key: string, width: number, height: number): React.ReactNode {
        return <IWidgetThingsBaseComponent
            key={key}
            width={width}
            height={height}
            WidgetData={this.WidgetData}
            WidgetDefaultValues={this.Description.DefaultValues}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SendPortEvent={this.SendPortEvent}
            inEditMode={this.Parent != null && this.Parent.inEditMode()}
            Macro={this.Description.JSMacros}
            StartWithLoading={this.Description.StartWithLoading}
            SupportEditing={this.Parent != null && this.Parent.SupportEditing}
            Padding={this.Description.WidgetPadding}
        >
            {this.Render(key, width, height)}
        </IWidgetThingsBaseComponent>
    }
    // --------------------------------------------------------------------------------------------
    get lastUpdateTS(): string {
        // Get settings
        const settings = this.Description.TrackUpdates;

        let lastUpdateTs: string;
        if (settings.IsExplicit ||
            _.isString(settings.Timestamp)) {
            // Use the provided timestamp.
            lastUpdateTs = settings.Timestamp;
        } else {
            // Get node.
            const node = WebNode.GetInstanceKey(this.NodeKey);
            if (!isDefined(node))
                return null;

            // Get thing.
            const thing = node.DBGMsgsRoot.Things[this.ThingKey];
            if (isDefined(thing)) {
                // Collect all timestamps.
                let timestamps = [];
                _.forEach(thing.Ports, port =>
                    _.forEach(port.States, state =>
                        timestamps.push(state.Timestamp)));

                // Get the most recent.
                if (timestamps.length > 0) {
                    timestamps.sort();
                    lastUpdateTs = _.last(timestamps);
                }
            }
        }
        return lastUpdateTs;
    }
    // --------------------------------------------------------------------------------------------
    get renderLastUpdateTs(): React.ReactNode {
        // Get settings
        const settings = this.Description.TrackUpdates;
        const lastUpdateTs = this.lastUpdateTS;

        // Return icon with last update timestamp.
        return <LastUpdateTs
            key={__LAST_UPDATE_TS_KEY__}
            ref={ref => {
                if (ref != null) {
                    const node = WebNode.GetInstanceKey(this.NodeKey);
                    node.lastUpdateTs[this.ThingKey] = ref;
                }
            }}
            ts={lastUpdateTs}
            explicitTs={settings.IsExplicit}
            dateFormat={settings.DateFormat}
            viewFormat={settings.ViewFormat} />;
    }
    // --------------------------------------------------------------------------------------------
    ToolbarDynamicTooltip() {
        const settings = this.Description.TrackUpdates;
        if (settings.ViewFormat == ITsViewFormat.Hover ||
            settings.ViewFormat == ITsViewFormat.Text) {
            return [<LastUpdateTsPrimitive
                key={`last-update-ts-${this.ThingKey}`}
                NodeKey={this.NodeKey}
                ThingKey={this.ThingKey}
                Settings={settings}
            />];
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    sendToolbarIconEvent = (info: ToolbarIconInfo) => {
        this.Thing.SendPortEvent(WebThingKeys.ToolbarIconClick, info.id, null);
    };
    // --------------------------------------------------------------------------------------------
    RenderBaseToolbar(key: string, toolbarHeight: number) {
        let reactNodes: React.ReactNode[] = [];

        // Append toolbar (if defined).
        const toolbar = this.RenderToolbar(key, toolbarHeight);
        if (toolbar != null)
            reactNodes.push(toolbar);

        // Add icon that the user have define
        if (this.Description.ToolbarIcons != null &&
            this.Description.ToolbarIcons.length > 0) {
            let icons = ToolbarIconInfo_ButtonRender(
                this.Description.ToolbarIcons,
                [], [], { padding: 4, cursor: "pointer" },
                this.sendToolbarIconEvent, null,
                _.noop, // TODO: Support infotip in toolbars !!!!
                _.noop);
            _.forEach(icons, x => reactNodes.push(x));
        }

        // TODO: implement it


        // Append last update timestamp (if configured).
        if (this.Description.TrackUpdates.ShowLastUpdateTs) {
            const lastUpdateTs = this.renderLastUpdateTs;
            if (lastUpdateTs != null)
                reactNodes.push(lastUpdateTs);
        } else {
            // Delete instance of last update timestamp (if exists).
            const node = WebNode.GetInstanceKey(this.NodeKey);
            if (isDefined(node) && _.has(node.lastUpdateTs, this.ThingKey))
                delete node.lastUpdateTs[this.ThingKey];
        }

        // Supports editing and in debug?
        if (this.Parent.SupportEditing && this.Parent.inDebug()) {
            reactNodes.push(<i
                key="bug-icon"
                style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: toolbarHeight + "px",
                    color: "rgba(0, 0, 0, 0.7)",
                    cursor: "pointer",
                    paddingLeft: 10
                }}
                className={"fa fa-bug"}
                onClick={() => {
                    this.Parent.DialogConf.show({
                        contentStaticSize: { w: 0.8, h: 0.95 },
                        actions: null,
                        body: <React.Suspense fallback={<LazyLoadingAnimation />}>
                            <DebugPortList widgetInst={this} />
                        </React.Suspense>,
                        title: "Port events: " + this.Description.Title,
                        handleClose: this.Parent.DialogConf.close
                    });
                }}
            />);

            reactNodes.push(<i
                key="link-icon"
                style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: toolbarHeight + "px",
                    color: "rgba(0, 0, 0, 0.7)",
                    cursor: "pointer",
                    paddingLeft: 10
                }}
                className={"fa fa-link"}
                onClick={() => {
                    this.Parent.DialogConf.show({
                        contentStaticSize: { w: 0.8, h: 0.95 },
                        actions: null,
                        body: <React.Suspense fallback={<LazyLoadingAnimation />}>
                            <DebugWidgetGemsList widgetInst={this} />
                        </React.Suspense>,
                        title: "Gems Usage: " + this.Description.Title,
                        handleClose: this.Parent.DialogConf.close
                    });
                }}
            />);
        }

        return reactNodes;
    }
    // --------------------------------------------------------------------------------------------
    public RenderBaseWidgetSettings(key: string, width: number, height: number, revision: number = 0): React.ReactElement<any> {
        return <SettingsWrapper
            key={key}
            revision={revision}
        >
            {this.RenderWidgetSettings(key, width, height)}
        </SettingsWrapper>;
    }
    // --------------------------------------------------------------------------------------------
    public RenderDescription(key: string, width: number, height: number, markdownProps: MarkdownProps = {}): React.ReactNode {
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <WidgetDescriptionAndPlayground
                width={width}
                height={height}
                key={key}
                widgetType={this.Description.Type}
                markdownProps={markdownProps}
            />
        </React.Suspense>;
    }
    // --------------------------------------------------------------------------------------------
    RenderBaseSettings(
        key: string,
        width: number,
        height: number,
        dialog: IWidgetDialog,
        onSaveSettings: (settings: WidgetThingGenericConfiguration) => void,
        revision?: number
    ): React.ReactNode {
        return <WidgetThingsSettings
            DescriptionJSXFunc={(width: number, height: number) => this.RenderDescription("documentation-dialog", width, height)}
            key={key}
            Dialog={dialog}
            Thing={this.Thing}
            WidgetDefaultValues={this.GetDefaultValues()}
            ValidateValues={(k, v) => this.ValidateValues(k, v)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            RenderWidgetSettings={this.RenderBaseWidgetSettings(key, width, height, revision)}
            GenericConfigurations={{ Description: this.Description }}
            WidgetData={this.WidgetData}
            onSaveSettings={onSaveSettings}
        />
    }
    // ================================================================================================ wrapper functions to widget ---

    // ================================================================================================ other functions +++
    // TODO: make the state to be the actual object and serialize only in low level
    SendPortEvent = (portKey: string, state: any) => {
        // Transform the data if we have custom form functions
        if (_.has(this.Description.PortMacros, portKey) &&
            _.has(this.Description.PortMacros[portKey], "onDataTransform") &&
            this.Description.PortMacros[portKey]["onDataTransform"].Enable) {
            Sandbox.GetInstance("WidgetThingsJSOutputRunner").Eval(
                this.Description.PortMacros[portKey]["onDataTransform"].Code,
                { PortState: JSON.parse(state), ExtraInfo: null, WidgetData: this.WidgetData } as WidgetDataTransform,
                (newState) => {
                    this.Thing.SendPortEvent(portKey, JSON.stringify(newState), state);
                },
                (error) => {
                    DebugEx.TraceError(error);
                }
            )
        } else {
            this.Thing.SendPortEvent(portKey, state, null);
        }
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Called to update widget data. Should not be overriden!
     * @param newWidgetData
     */
    WidgetDataUpdate(newWidgetData: any) {
        this.WidgetData = _.cloneDeep(newWidgetData);
        this.updateThing();
        if (this.Parent != null) {
            this.Parent.CheckForUpdatedData(this.Description.Key, newWidgetData);
        }
        this.UpdateLocalization();
    }
    // ------------------------------------------------------------------------------------------------
    public UpdateLocalization() {
        let dictionaryMap = this.GetLocalizationDictionary();
        let locInstance = Localization.GetInstance(this.NodeKey);
        if (locInstance != null) {
            locInstance.ReplaceTexts(locInstance.DefaultLanguage, this.ThingKey, dictionaryMap);
        } else {
            DebugEx.TraceError("Failed to find localization for the nodeKey", this.NodeKey);
        }
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizedWidgetTitle() {
        let locInstance = Localization.GetInstance(this.NodeKey);
        // if localization is defined
        if (locInstance != null) {

            // try get map for this widget
            let widgetMap = locInstance.GetTextMap(this.ThingKey);

            // if localization Map defined
            if (isDefined(widgetMap)) {
                // try widget's title
                const label = widgetMap[WidgetLocalizationKeys.Title];
                if (!IsNullOrEmpty(label)) {
                    return label;
                }
            }

        } else {
            DebugEx.TraceError("Failed to find localization for the nodeKey", this.NodeKey);
        }
        return null;
    }
    // ================================================================================================ other functions ---
}
// ------------------------------------------------------------------------------------------------
interface SettingsWrapperProps {
    revision?: number;
}
// ------------------------------------------------------------------------------------------------
class SettingsWrapper extends React.Component<SettingsWrapperProps>{
    static whyDidYouRender = true;
    // ------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    shouldComponentUpdate(nextProps: SettingsWrapperProps) {
        return this.props.revision != nextProps.revision;
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        if (isDefined(this.props.children))
            return <div>
                {this.props.children}
            </div>
        return null;
    }
    // ------------------------------------------------------------------------------------------------
}
